import React from "react";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";

import {Divider} from "@/components/divider/Divider.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {MultiMaintenanceScheduleSelect} from "@/components/multiMantenanceScheduleSelect/MultiMaintenanceScheduleSelect.tsx";
import {SelectionCard} from "@/components/selectionCard/SelectionCard.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {ServiceMaintenanceState} from "@/lib/types/checks.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {CheckMaintenanceV2FormValues} from "@/pages/checks/update/CreateCheckForm.types.ts";

import {ManualMaintenanceAlert} from "./ManualMaintenanceAlert.tsx";
import {ScheduledMaintenanceAlert} from "./ScheduledMaintenanceAlert.tsx";

import styles from "./BulkSetMaintenanceFormV2.module.scss";

export interface BulkSetMaintenanceFormV2Props {
  formId: string;
  onSubmit: (data: CheckMaintenanceV2FormValues) => void;
  onError?: (errors: FieldErrors<CheckMaintenanceV2FormValues>, event?: React.BaseSyntheticEvent) => void;
  serverErrors?: ServerErrors;
  services?: ServiceMaintenanceState[];
}

enum SelectedOperationType {
  MANUAL = "MANUAL",
  SCHEDULED = "SCHEDULED",
}

export const BulkSetMaintenanceFormV2 = ({
  formId,
  onSubmit,
  onError,
  serverErrors,
  services,
}: BulkSetMaintenanceFormV2Props) => {
  const methods = useForm<CheckMaintenanceV2FormValues>({
    defaultValues: {
      is_under_manual_maintenance: true,
      pause_under_manual_maintenance: false,
      maintenance_schedule_ids: [],
    },
  });
  const {watch, handleSubmit, setValue} = methods;
  const handleChange = (newValue: string) => {
    const isUnderManualMaintenance = newValue === SelectedOperationType.MANUAL;
    setValue(ServiceField.IS_UNDER_MANUAL_MAINTENANCE, isUnderManualMaintenance, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const manualMaintenanceSelected = watch(ServiceField.IS_UNDER_MANUAL_MAINTENANCE);
  const pauseUnderManualMaintenance = watch(ServiceField.PAUSE_UNDER_MANUAL_MAINTENANCE);
  const maintenanceScheduleIds = watch(ServiceField.MAINTENANCE_SCHEDULE_IDS) || [];
  const selectedOperationType = manualMaintenanceSelected
    ? SelectedOperationType.MANUAL
    : SelectedOperationType.SCHEDULED;
  return (
    <FormProvider {...methods}>
      <form id={formId} onSubmit={handleSubmit(onSubmit, onError)}>
        {serverErrors?.schedule_entries && <FormErrorAlerts errors={serverErrors?.schedule_entries} />}
        <SelectionCard value={selectedOperationType} layout={"vertical"} onChange={handleChange}>
          <SelectionCard.Option
            value={SelectedOperationType.MANUAL}
            title="Under Maintenance Now"
            description="Alerts will be suppressed until you change this setting"
            labelPosition={"right"}
          >
            {manualMaintenanceSelected && (
              <>
                <Divider className="my-2" />
                <SwitchField
                  name={ServiceField.PAUSE_UNDER_MANUAL_MAINTENANCE}
                  label={"Stop Check execution during maintenance"}
                  labelPosition={"left"}
                  labelTypography={"bodySmall"}
                  description={
                    "Pauses checks during maintenance. When off, checks still run but alerts are suppressed."
                  }
                  className={styles.pauseSwitch}
                />
              </>
            )}
          </SelectionCard.Option>
          <SelectionCard.Option
            value={SelectedOperationType.SCHEDULED}
            title="Link Maintenance Schedules"
            description="Alerts will be suppressed based on the linked schedule windows"
            labelPosition={"right"}
          />
        </SelectionCard>
        {!manualMaintenanceSelected && (
          <MultiMaintenanceScheduleSelect
            className="mt-4"
            defaultValue={maintenanceScheduleIds}
            onReplace={(values) => {
              setValue(ServiceField.MAINTENANCE_SCHEDULE_IDS, values, {shouldDirty: true});
            }}
            onAppend={(value) => {
              if (!maintenanceScheduleIds.includes(value)) {
                setValue(ServiceField.MAINTENANCE_SCHEDULE_IDS, [...maintenanceScheduleIds, value], {
                  shouldDirty: true,
                });
              }
            }}
            tags={[]}
            variant={"short"}
          />
        )}
        {services && services.length > 0 && manualMaintenanceSelected && (
          <ManualMaintenanceAlert services={services} pauseRequired={pauseUnderManualMaintenance} />
        )}
        {services && services.length > 0 && !manualMaintenanceSelected && (
          <ScheduledMaintenanceAlert services={services} scheduleIds={maintenanceScheduleIds} />
        )}
      </form>
    </FormProvider>
  );
};
